import {
  AutomationType,
  TriggerType,
  TriggerTypeClient,
  TriggerList,
  TriggerListClient,
  AutomationTriggerList,
  AutomationTriggerListClient,
  AutomationTriggerListJobBased,
} from 'helpers/enum';
import {
  ACTIVITY_LABEL_MAP,
  makeActivityTriggerItem,
  findTrigger,
  findTriggerById,
  liveFirst,
} from './helpers';
import { isActivityTrigger, isJobPlacementActivityTrigger, surveyFormActivityHint } from 'helpers/surveyFormActivityTriggers';

export function buildTriggerGroups(sortedTriggers, automationType, triggerType) {
  const isClient = automationType === AutomationType.Client;

  const activityTriggerId = isClient ? 1 : 12;
  const activityTrigger =
    sortedTriggers.find(t => t.triggerId === activityTriggerId) ||
    (isClient ? AutomationTriggerListClient[0] : AutomationTriggerList[0]);

  // Engagement items (email + SMS)
  const engagementActivityIds = [6, 5, 17, 4, 23, 15, 7, 8, 24, 16, 26];
  const engagementItems = engagementActivityIds.map(id => ({
    label: ACTIVITY_LABEL_MAP[id],
    status: 'live',
    data: makeActivityTriggerItem(activityTrigger, id, ACTIVITY_LABEL_MAP[id]),
  }));
  const roiScoreTrigger = findTrigger(sortedTriggers, 'Roi score');
  engagementItems.push({ label: 'Roi score', status: roiScoreTrigger ? 'live' : 'soon', data: roiScoreTrigger });

  // Website items
  const websiteActivityIds = [22, 20, 21];
  const websiteItems = websiteActivityIds.map(id => ({
    label: ACTIVITY_LABEL_MAP[id],
    status: 'live',
    data: makeActivityTriggerItem(activityTrigger, id, ACTIVITY_LABEL_MAP[id]),
  }));
  const clientWebsiteItems = [
    { label: 'Page view', status: 'live', data: makeActivityTriggerItem(activityTrigger, 22, 'Page view') },
    { label: 'Custom view', status: 'live', data: makeActivityTriggerItem(activityTrigger, 25, 'Custom view') },
  ];

  // Surveys / Forms items
  // Survey opened / Survey completed / Form completed use the campaign type's Activity trigger: the base one
  // on Candidate and Contact campaigns, the job-based or placement-based one (ROI-356) on the other four.
  const surveyFormActivityTrigger =
    sortedTriggers.find(t => isJobPlacementActivityTrigger(t.triggerId, automationType)) ||
    sortedTriggers.find(t => t.triggerId === activityTriggerId) ||
    null;
  const activityTriggerFound = surveyFormActivityTrigger !== null;
  const surveyOpenedData = activityTriggerFound ? makeActivityTriggerItem(surveyFormActivityTrigger, 18, 'Survey Opened') : null;
  const surveyCompletedData = activityTriggerFound ? makeActivityTriggerItem(surveyFormActivityTrigger, 19, 'Survey completed') : null;
  const formCompletedData = activityTriggerFound ? makeActivityTriggerItem(surveyFormActivityTrigger, 29, 'Form completed') : null;
  const surveyScoreTrigger = findTrigger(sortedTriggers, 'Survey score');
  const formScoreTrigger = findTrigger(sortedTriggers, 'Form score');
  const hint = activityId =>
    activityTriggerFound ? surveyFormActivityHint(surveyFormActivityTrigger.triggerId, automationType, activityId) : null;
  const surveysFormsItems = [
    { label: 'Survey Opened', status: activityTriggerFound ? 'live' : 'soon', data: surveyOpenedData, hint: hint(18) },
    { label: 'Survey completed', status: activityTriggerFound ? 'live' : 'soon', data: surveyCompletedData, hint: hint(19) },
    { label: 'Form completed', status: activityTriggerFound ? 'live' : 'soon', data: formCompletedData, hint: hint(29) },
    { label: 'Survey score', status: surveyScoreTrigger ? 'live' : 'soon', data: surveyScoreTrigger },
    { label: 'Form score', status: formScoreTrigger ? 'live' : 'soon', data: formScoreTrigger },
  ];
  if (!isClient && triggerType === TriggerType.CandidateBased) {
    const npsTrigger = findTrigger(sortedTriggers, 'Net promoter score');
    surveysFormsItems.push({ label: 'Net promoter score', status: npsTrigger ? 'live' : 'soon', data: npsTrigger });
  }
  if (isClient && triggerType === TriggerTypeClient.ClientBased) {
    const npsTrigger = findTrigger(sortedTriggers, 'Net promoter score');
    surveysFormsItems.push({ label: 'Net promoter score', status: npsTrigger ? 'live' : 'soon', data: npsTrigger });
  }

  const activitiesItems = [{ label: 'Activity logged', status: 'soon', data: null }];

  const customFieldItems = sortedTriggers
    .filter(t => t.customFieldTriggerId !== null && t.customFieldTriggerId !== undefined)
    .map(t => ({ label: t.triggerName, status: 'live', data: t }));

  // Job Custom Fields and Application Custom Fields get their own separate sections (candidate-only) rather
  // than being merged, so split them out of the generic customFieldItems by triggerId.
  const jobCustomFieldTriggerIds = [TriggerList.JobCustomFieldList, TriggerList.JobCustomFieldMultiSelectList, TriggerList.JobCustomFieldDate];
  const applicationCustomFieldTriggerIds = [TriggerList.ApplicationCustomFieldList, TriggerList.ApplicationCustomFieldMultiSelectList, TriggerList.ApplicationCustomFieldDate];
  const jobCustomFieldItems = sortedTriggers
    .filter(t => jobCustomFieldTriggerIds.includes(t.triggerId))
    .map(t => ({ label: t.triggerName, status: 'live', data: t }));
  const applicationCustomFieldItems = sortedTriggers
    .filter(t => applicationCustomFieldTriggerIds.includes(t.triggerId))
    .map(t => ({ label: t.triggerName, status: 'live', data: t }));

  let groups = [];

  if (isClient) {
    groups = buildClientTriggerGroups(sortedTriggers, triggerType, {
      engagementItems, clientWebsiteItems, surveysFormsItems, activitiesItems, customFieldItems,
    });
  } else {
    groups = buildCandidateTriggerGroups(sortedTriggers, triggerType, {
      engagementItems, websiteItems, surveysFormsItems, activitiesItems, customFieldItems,
      jobCustomFieldItems, applicationCustomFieldItems,
    });
  }

  function getSelectedLabel(formTriggerItem) {
    if (!formTriggerItem || !formTriggerItem.firstColumn) return null;
    const id = formTriggerItem.firstColumn.id;
    const activities = formTriggerItem.thirdColumn && formTriggerItem.thirdColumn.activities;
    if (isActivityTrigger(id, automationType) && Array.isArray(activities) && activities.length === 1 && ACTIVITY_LABEL_MAP[activities[0]]) {
      return ACTIVITY_LABEL_MAP[activities[0]];
    }
    return formTriggerItem.firstColumn.name || null;
  }

  return { groups, getSelectedLabel };
}

function buildClientTriggerGroups(sortedTriggers, triggerType, shared) {
  const { engagementItems, clientWebsiteItems, surveysFormsItems, activitiesItems, customFieldItems } = shared;

  const contactFieldDefs = [
    { label: 'Status', triggerName: 'Status' },
    { label: 'Skills', triggerName: 'Skills' },
    { label: 'Position', triggerName: 'Position' },
    { label: 'Email', triggerName: 'Contact Email' },
    { label: 'Other email', triggerName: 'Contact Other email' },
    { label: 'Phone', triggerName: 'Contact Phone' },
    { label: 'Mobile', triggerName: 'Contact Mobile' },
    { label: 'Notes', triggerName: 'Notes' },
    { label: 'Date created', triggerName: 'Date created' },
    { label: 'Date updated', triggerName: 'Date updated' },
    { label: 'Unsubscribed', triggerId: TriggerListClient.ClientUnsubscribe },
  ];
  const contactFieldsItems = liveFirst(contactFieldDefs.map(def => {
    if (def.forceSoon) return { label: def.label, status: 'soon', data: null };
    const t = def.triggerId
      ? findTriggerById(sortedTriggers, def.triggerId)
      : findTrigger(sortedTriggers, def.triggerName);
    return { label: def.label, status: t ? 'live' : 'soon', data: t };
  }));

  const companyNameTrigger = findTrigger(sortedTriggers, 'Company');
  const companyCityTrigger = findTrigger(sortedTriggers, 'City');
  const companyStateTrigger = findTrigger(sortedTriggers, 'State');
  const companyPostcodeTrigger = findTrigger(sortedTriggers, 'Postal Code');
  const companyCountryTrigger = findTrigger(sortedTriggers, 'Country');
  const companyFieldsItems = [
    { label: 'Company name', status: companyNameTrigger ? 'live' : 'soon', data: companyNameTrigger },
    { label: 'City', status: companyCityTrigger ? 'live' : 'soon', data: companyCityTrigger },
    { label: 'State', status: companyStateTrigger ? 'live' : 'soon', data: companyStateTrigger },
    { label: 'Postcode', status: companyPostcodeTrigger ? 'live' : 'soon', data: companyPostcodeTrigger },
    { label: 'Country', status: companyCountryTrigger ? 'live' : 'soon', data: companyCountryTrigger },
    { label: 'Status is', status: 'soon', data: null },
    { label: 'Status changed to', status: 'soon', data: null },
    { label: 'Skill is', status: 'soon', data: null },
    { label: 'Created date', status: 'soon', data: null },
    { label: 'Updated date', status: 'soon', data: null },
    { label: 'Custom field', status: 'soon', data: null },
    { label: 'Parent', status: 'request', data: null },
    { label: 'In folder', status: 'request', data: null },
  ];

  const opportunityStage = findTrigger(sortedTriggers, 'Opportunity stage');
  const opportunitiesItems = [
    { label: 'Opportunity stage', status: opportunityStage ? 'live' : 'soon', data: opportunityStage },
  ];

  if (triggerType === TriggerTypeClient.ClientBased) {
    const aggregateJobStatusTrigger = findTriggerById(sortedTriggers, TriggerListClient.ClientAggregateJobStatus);
    const aggregatePlacementStatusTrigger = findTriggerById(sortedTriggers, TriggerListClient.ClientAggregatePlacementStatus);
    return [
      { name: 'Engagement', items: engagementItems },
      { name: 'Website', items: clientWebsiteItems },
      { name: 'Surveys / Forms', items: surveysFormsItems },
      { name: 'Contact fields', items: contactFieldsItems },
      { name: 'Company fields', items: liveFirst(companyFieldsItems) },
      { name: 'Opportunities', items: opportunitiesItems },
      { name: 'Jobs', items: [{ label: 'Job status', status: aggregateJobStatusTrigger ? 'live' : 'soon', data: aggregateJobStatusTrigger }] },
      { name: 'Placements', items: [{ label: 'Placement status', status: aggregatePlacementStatusTrigger ? 'live' : 'soon', data: aggregatePlacementStatusTrigger }] },
      { name: 'Activities', items: activitiesItems },
      ...(customFieldItems.length > 0 ? [{ name: 'Contact Custom Fields', items: customFieldItems }] : []),
    ];
  }

  if (triggerType === TriggerTypeClient.JobBased) {
    const jobFields = [
      { label: 'Job status', status: findTrigger(sortedTriggers, 'Job status') ? 'live' : 'soon', data: findTrigger(sortedTriggers, 'Job status') },
      { label: 'Application status', status: findTrigger(sortedTriggers, 'Application Status') ? 'live' : 'soon', data: findTrigger(sortedTriggers, 'Application Status') },
      { label: 'Job application received', status: 'soon', data: null },
      { label: 'Interview date', status: findTrigger(sortedTriggers, 'Interview date') ? 'live' : 'soon', data: findTrigger(sortedTriggers, 'Interview date') },
      { label: 'Job work type', status: findTrigger(sortedTriggers, 'Job work type') ? 'live' : 'soon', data: findTrigger(sortedTriggers, 'Job work type') },
      { label: 'Job category', status: findTrigger(sortedTriggers, 'Job Category') ? 'live' : 'soon', data: findTrigger(sortedTriggers, 'Job Category') },
      { label: 'Job type', status: findTrigger(sortedTriggers, 'Job Type') ? 'live' : 'soon', data: findTrigger(sortedTriggers, 'Job Type') },
      { label: 'Job IDs', status: findTrigger(sortedTriggers, 'Job IDs') ? 'live' : 'soon', data: findTrigger(sortedTriggers, 'Job IDs') },
      { label: 'Job location', status: findTrigger(sortedTriggers, 'Job location') ? 'live' : 'soon', data: findTrigger(sortedTriggers, 'Job location') },
      { label: 'Job Date created', status: findTrigger(sortedTriggers, 'Job Date created') ? 'live' : 'soon', data: findTrigger(sortedTriggers, 'Job Date created') },
      { label: 'Job Date updated', status: findTrigger(sortedTriggers, 'Job Date updated') ? 'live' : 'soon', data: findTrigger(sortedTriggers, 'Job Date updated') },
      { label: 'Job skills', status: 'request', data: null },
      { label: 'Job start date', status: 'request', data: null },
      { label: 'Job end date', status: 'request', data: null },
      { label: 'Job source', status: 'request', data: null },
      { label: 'Number of jobs', status: 'request', data: null },
    ];
    return [
      { name: 'Job fields', items: liveFirst(jobFields) },
      { name: 'Surveys / Forms', items: liveFirst(surveysFormsItems) },
      { name: 'Activities', items: activitiesItems },
      ...(customFieldItems.length > 0 ? [{ name: 'Job Custom Fields', items: customFieldItems }] : []),
    ];
  }

  if (triggerType === TriggerTypeClient.PlacementBased) {
    const placementFields = [
      { label: 'Start date', status: findTrigger(sortedTriggers, 'Start date') ? 'live' : 'soon', data: findTrigger(sortedTriggers, 'Start date') },
      { label: 'End date', status: findTrigger(sortedTriggers, 'End date') ? 'live' : 'soon', data: findTrigger(sortedTriggers, 'End date') },
      { label: 'Date created', status: findTrigger(sortedTriggers, 'Created date') ? 'live' : 'soon', data: findTrigger(sortedTriggers, 'Created date') },
      { label: 'Placement status', status: findTrigger(sortedTriggers, 'Placement status') ? 'live' : 'soon', data: findTrigger(sortedTriggers, 'Placement status') },
      { label: 'Placement type', status: findTrigger(sortedTriggers, 'Placement type') ? 'live' : 'soon', data: findTrigger(sortedTriggers, 'Placement type') },
      { label: 'Placement approved', status: 'request', data: null },
      { label: 'Placement source', status: 'request', data: null },
    ];
    return [
      { name: 'Placement fields', items: liveFirst(placementFields) },
      { name: 'Surveys / Forms', items: liveFirst(surveysFormsItems) },
      { name: 'Activities', items: activitiesItems },
      ...(customFieldItems.length > 0 ? [{ name: 'Placement Custom Fields', items: customFieldItems }] : []),
    ];
  }

  // Fallback
  return [
    { name: 'Engagement', items: engagementItems },
    { name: 'Website', items: clientWebsiteItems },
    { name: 'Surveys / Forms', items: surveysFormsItems },
    { name: 'Contact fields', items: contactFieldsItems },
    { name: 'Company fields', items: companyFieldsItems },
    { name: 'Opportunities', items: opportunitiesItems },
    { name: 'Activities', items: activitiesItems },
    ...(customFieldItems.length > 0 ? [{ name: 'Contact Custom Fields', items: customFieldItems }] : []),
  ];
}

function buildCandidateTriggerGroups(sortedTriggers, triggerType, shared) {
  const { engagementItems, websiteItems, surveysFormsItems, activitiesItems, customFieldItems, jobCustomFieldItems, applicationCustomFieldItems } = shared;

  if (triggerType === TriggerType.CandidateBased) {
    const candidateFieldDefs = [
      { label: 'Status', triggerName: 'Status' },
      { label: 'Rating', triggerName: 'Rating' },
      { label: 'Skills', triggerName: 'Skills' },
      { label: 'Availability', triggerName: 'Availability' },
      { label: 'Current position', triggerName: 'Current Position' },
      { label: 'Current employer', triggerName: 'Current Employer' },
      { label: 'Email', triggerName: 'Email' },
      { label: 'Secondary email', triggerName: 'Secondary email' },
      { label: 'Phone', triggerName: 'Phone' },
      { label: 'Mobile', triggerName: 'Mobile' },
      { label: 'City', triggerName: 'City' },
      { label: 'State', triggerName: 'State' },
      { label: 'Postal code', triggerName: 'Postal Code' },
      { label: 'Country', triggerName: 'Country' },
      { label: 'Birthday', triggerName: 'Birthday' },
      { label: 'Notes', triggerName: 'Notes' },
      { label: 'Job application date', triggerName: 'Job application date' },
      { label: 'Resume date', triggerName: 'Resume date' },
      { label: 'Document expiry date', triggerName: 'Document Expiry Date' },
      { label: 'Document type', triggerName: 'Document Type' },
      { label: 'Date created', triggerName: 'Date created' },
      { label: 'Date updated', triggerName: 'Date updated' },
      { label: 'Unsubscribed', triggerId: TriggerList.CandidateUnsubscribe },
      { label: 'Source', triggerId: TriggerList.CandidateSource },
      { label: 'Contact method', triggerId: TriggerList.CandidateContactMethod },
      { label: 'Seeking', triggerId: TriggerList.CandidateSeeking, hideIfMissing: true },
    ];
    const candidateFieldItemsRaw = candidateFieldDefs.flatMap(def => {
      const t = def.triggerId
        ? findTriggerById(sortedTriggers, def.triggerId)
        : findTrigger(sortedTriggers, def.triggerName);
      // Not in this ATS (e.g. Seeking on Bullhorn): omit rather than show as coming soon
      if (!t && def.hideIfMissing) return [];
      return [{ label: def.label, status: t ? 'live' : 'soon', data: t }];
    });
    const candidateFieldItems = liveFirst(candidateFieldItemsRaw);

    return [
      { name: 'Engagement', items: engagementItems },
      { name: 'Website', items: websiteItems },
      { name: 'Surveys / Forms', items: surveysFormsItems },
      { name: 'Candidate fields', items: candidateFieldItems },
      { name: 'Activities', items: activitiesItems },
      ...(customFieldItems.length > 0 ? [{ name: 'Candidate Custom Fields', items: customFieldItems }] : []),
    ];
  }

  if (triggerType === TriggerType.JobBased) {
    const appStatusFallback = AutomationTriggerListJobBased.find(t => t.triggerName === 'Application status');
    const jobFieldDefs = [
      { label: 'Job status', triggerName: 'Job status' },
      { label: 'Application status', triggerName: 'Application Status', fallback: appStatusFallback },
      { label: 'Job application received', triggerName: 'Job application received' },
      { label: 'Interview date', triggerName: 'Interview date' },
      { label: 'Job work type', triggerName: 'Job work type' },
      { label: 'Job category', triggerName: 'Job category' },
      { label: 'Job type', triggerName: 'Job type' },
      { label: 'Job IDs', triggerName: 'Job IDs' },
      { label: 'Job location', triggerName: 'Job location' },
    ];
    const jobFieldItems = jobFieldDefs.map(def => {
      const t = findTrigger(sortedTriggers, def.triggerName) || def.fallback || null;
      return { label: def.label, status: t ? 'live' : 'soon', data: t };
    });
    const jobDateCreatedTrigger = findTrigger(sortedTriggers, 'Job Date created');
    const jobDateUpdatedTrigger = findTrigger(sortedTriggers, 'Job Date updated');
    jobFieldItems.push({ label: 'Job Date created', status: jobDateCreatedTrigger ? 'live' : 'soon', data: jobDateCreatedTrigger });
    jobFieldItems.push({ label: 'Job Date updated', status: jobDateUpdatedTrigger ? 'live' : 'soon', data: jobDateUpdatedTrigger });
    jobFieldItems.push({ label: 'Job skills', status: 'request', data: null });
    jobFieldItems.push({ label: 'Job start date', status: 'request', data: null });
    jobFieldItems.push({ label: 'Job end date', status: 'request', data: null });
    jobFieldItems.push({ label: 'Job source', status: 'request', data: null });
    jobFieldItems.push({ label: 'Number of jobs', status: 'request', data: null });

    const docExpiry = findTrigger(sortedTriggers, 'Document Expiry Date');
    return [
      { name: 'Job fields', items: liveFirst(jobFieldItems) },
      { name: 'Candidate fields', items: [{ label: 'Document expiry date', status: docExpiry ? 'live' : 'soon', data: docExpiry }] },
      { name: 'Surveys / Forms', items: liveFirst(surveysFormsItems) },
      { name: 'Activities', items: activitiesItems },
      ...(jobCustomFieldItems.length > 0 ? [{ name: 'Job Custom Fields', items: jobCustomFieldItems }] : []),
      ...(applicationCustomFieldItems.length > 0 ? [{ name: 'Application Custom Fields', items: applicationCustomFieldItems }] : []),
    ];
  }

  if (triggerType === TriggerType.PlacementBased) {
    const placementFieldDefs = [
      { label: 'Start date', triggerName: 'Start date' },
      { label: 'End date', triggerName: 'End date' },
      { label: 'Date created', triggerName: 'Created date' },
      { label: 'Placement status', triggerName: 'Placement status' },
    ];
    const placementFieldItems = placementFieldDefs.map(def => {
      const t = findTrigger(sortedTriggers, def.triggerName);
      return { label: def.label, status: t ? 'live' : 'soon', data: t };
    });
    const placementTypeTrigger = findTrigger(sortedTriggers, 'Placement type');
    placementFieldItems.push({ label: 'Placement type', status: placementTypeTrigger ? 'live' : 'soon', data: placementTypeTrigger });
    placementFieldItems.push({ label: 'Placement approved', status: 'request', data: null });
    placementFieldItems.push({ label: 'Placement source', status: 'request', data: null });

    const docExpiry =
      findTrigger(sortedTriggers, 'Document Expiry Date') ||
      findTrigger(sortedTriggers, 'Placement document expiry date');

    return [
      { name: 'Placement fields', items: liveFirst(placementFieldItems) },
      { name: 'Candidate fields', items: [{ label: 'Document expiry date', status: docExpiry ? 'live' : 'soon', data: docExpiry }] },
      { name: 'Surveys / Forms', items: liveFirst(surveysFormsItems) },
      { name: 'Activities', items: activitiesItems },
      ...(customFieldItems.length > 0 ? [{ name: 'Placement Custom Fields', items: customFieldItems }] : []),
    ];
  }

  // Fallback
  return [
    { name: 'Engagement', items: engagementItems },
    { name: 'Website', items: websiteItems },
    { name: 'Surveys / Forms', items: surveysFormsItems },
    { name: 'Activities', items: activitiesItems },
    ...(customFieldItems.length > 0 ? [{ name: 'Candidate Custom Fields', items: customFieldItems }] : []),
  ];
}
